import React, { useContext, useState, useEffect } from "react";
import { CartContext } from "../context/CartContext";
import { Link, useNavigate } from "react-router-dom";
import { fetchDeliveryZones, submitOrder } from "../api/api";

function CartPage() {
  const { cart, removeFromCart, clearCart, updateQuantity } = useContext(CartContext);
  const navigate = useNavigate();

  const [deliveryMethod, setDeliveryMethod] = useState(null);
  const [zones, setZones] = useState([]);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [phone, setPhone] = useState("");
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [lastOrderSummary, setLastOrderSummary] = useState(null);
  const [toast, setToast] = useState(null);

  const total = cart.reduce((acc, item) => acc + item.total_price, 0); 
  const selectedZone = zones.find((z) => z.id === selectedZoneId);
  const deliveryFee = selectedZone ? selectedZone.delivery_fee : 0;
  const totalWithDelivery = total + deliveryFee;

  useEffect(() => {
    if (showDeliveryModal) {
      fetchDeliveryZones().then(setZones);
    }
  }, [showDeliveryModal]);

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 2500);
  };

  const handleDeliveryClick = () => {
    setDeliveryMethod("delivery");
    setShowDeliveryModal(true);
  };

  const handleDeliveryConfirm = () => {
    if (!customerName || !selectedZoneId || !address || !phone) {
      showToast("⚠️ يرجى تعبئة جميع الحقول المطلوبة.");
      return;
    }
    setShowDeliveryModal(false);
  };

  const handleSubmitOrder = async () => {
    if (!deliveryMethod) {
      showToast("⚠️ يرجى اختيار طريقة الاستلام.");
      return;
    }

    if (!customerName || !selectedZone || !address || !phone) {
      showToast("⚠️ يرجى تعبئة بيانات التوصيل كاملة.");
      return;
    }

    const orderData = {
      customer_name: customerName,
      phone,
      address,
      notes,
      delivery_method: deliveryMethod,
      delivery_zone_id: selectedZoneId,
      delivery_fee: deliveryFee,
      subtotal: total,
      total: totalWithDelivery,
      items: cart.map((item) => ({
        product_id: item.product_id || item.id,
        name: item.name,
        price: item.base_price || 0,
        quantity: item.quantity,
        total_price: item.total_price,
        selected_options: item.selected_options || {},
      })),
    };

    try {
      await submitOrder(orderData);
      setLastOrderSummary({
        subtotal: total.toFixed(2),
        delivery: deliveryFee.toFixed(2),
        total: totalWithDelivery.toFixed(2),
      });
      clearCart();
      setOrderSuccess(true);
    } catch (error) {
      showToast("❌ فشل إرسال الطلب. حاول مرة أخرى.");
      console.error(error.response?.data || error);
    }
  };

  if (orderSuccess && lastOrderSummary) {
    return (
      <div style={styles.successContainer}>
        <h2 style={styles.successTitle}>✅ تم إرسال الطلب بنجاح!</h2>
        <p style={styles.successText}>📦 سيتم تجهيز طلبك وإرساله قريبًا.</p>
        <div style={styles.summaryBox}>
          <p><strong>📦 الطلبات:</strong> {lastOrderSummary.subtotal}₪</p>
          <p><strong>🚚 التوصيل:</strong> {lastOrderSummary.delivery}₪</p>
          <p><strong>💵 الإجمالي:</strong> {lastOrderSummary.total}₪</p>
        </div>
        <Link to="/categories" style={styles.continueButton}>🔁 متابعة التسوق</Link>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {toast && <div style={styles.toast}>{toast}</div>}

      <header style={styles.header}>
        <button onClick={() => navigate(-1)} style={styles.backButton}>← رجوع</button>
        <h1 style={styles.pageTitle}>سلة المشتريات</h1>
      </header>

      {cart.length === 0 ? (
        <p style={styles.emptyCart}>⚠️ السلة فارغة. <Link to="/categories" style={styles.link}>تصفح المنتجات</Link></p>
      ) : (
        <>
          <ul style={styles.cartList}>
            {cart.map((item, index) => (
              <li key={index} style={styles.cartItem}>
                <img src={`https://al-asala-restaurant.com/api/storage/${item.image}`} style={styles.itemImage} alt={item.name} />
                <div style={styles.itemContent}>
                  <h3 style={styles.itemTitle}>{item.name}</h3>
                  <div style={styles.itemActions}>
                    <div style={styles.quantityControl}>
                      <button onClick={() => updateQuantity(index, item.quantity - 1)} style={styles.quantityBtn}>-</button>
                      <span>{item.quantity}</span>
                      <button onClick={() => updateQuantity(index, item.quantity + 1)} style={styles.quantityBtn}>+</button>
                    </div>
                    <p style={styles.itemPrice}>💰 {item.total_price.toFixed(2)}₪</p>
                  </div>
                  {item.selected_options && (
                    <div style={styles.optionList}>
                      {Object.values(item.selected_options).flat().map((opt, i) => (
                        <div key={i}>🧩 {opt.name} ({opt.price_diff}₪)</div>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={() => removeFromCart(index)} style={styles.removeBtn}>🗑️</button>
              </li>
            ))}
          </ul>

          {/* ملخص */}
          <div style={styles.summarySection}>
            <p><strong>📦 المنتجات:</strong> {total.toFixed(2)}₪</p>
            {selectedZone && (
              <>
                <p><strong>🚚 التوصيل:</strong> {deliveryFee}₪</p>
                <p><strong>💰 الإجمالي:</strong> {totalWithDelivery.toFixed(2)}₪</p>
              </>
            )}
          </div>

          {/* اختيار طريقة التوصيل */}
          <div style={styles.deliverySection}>
            <h3>📍 طريقة الاستلام:</h3>
            <button
              onClick={handleDeliveryClick}
              style={{
                ...styles.methodButton,
                backgroundColor: deliveryMethod === "delivery" ? "#2196f3" : "#d0d9ff",
                color: deliveryMethod === "delivery" ? "#fff" : "#1a237e"
              }}
            >
              🚚 توصيل للمنزل
            </button>
          </div>

          {/* زر إرسال */}
          {deliveryMethod && (
            <button onClick={handleSubmitOrder} style={styles.submitButton}>📤 إرسال الطلب</button>
          )}

          <button onClick={clearCart} style={styles.clearButton}>🧹 تفريغ السلة</button>
        </>
      )}

      {/* نافذة التوصيل */}
      {showDeliveryModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>🚚 بيانات التوصيل</h2>

            <input
              type="text"
              placeholder="👤 اسم العميل"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              style={styles.modalInput}
            />

            <select
              value={selectedZoneId || ""}
              onChange={(e) => setSelectedZoneId(parseInt(e.target.value))}
              style={styles.modalSelect}
            >
              <option value="" disabled>اختر منطقة التوصيل</option>
              {zones.map((zone) => (
                <option key={zone.id} value={zone.id}>{zone.name} ({zone.delivery_fee}₪)</option>
              ))}
            </select>

            <input
              type="text"
              placeholder="📬 العنوان"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              style={styles.modalInput}
            />

            <input
              type="text"
              placeholder="📞 رقم الهاتف"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={styles.modalInput}
            />

            <textarea
              placeholder="📝 ملاحظات إضافية (اختياري)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={styles.modalTextArea}
              rows={3}
            />

            <button onClick={handleDeliveryConfirm} style={styles.modalConfirmButton}>✅ تأكيد</button>
            <button onClick={() => setShowDeliveryModal(false)} style={styles.modalCancelButton}>❌ إلغاء</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* 🌟 Styles */
const styles = {
  container: {
    fontFamily: "'Cairo', sans-serif", backgroundColor: "#fffcf6", minHeight: "100vh",
    padding: "20px", color: "#4a403a"
  },
  header: { display: "flex", alignItems: "center", marginBottom: "15px" },
  backButton: {
    backgroundColor: "#f2e7dc", color: "#6d5747", padding: "8px 14px", borderRadius: "8px",
    cursor: "pointer", border: "none"
  },
  pageTitle: {
    flex: 1, textAlign: "center", fontSize: "22px", fontWeight: "700", color: "#6d5747"
  },
  emptyCart: { textAlign: "center", fontSize: "16px", marginTop: "40px" },
  link: { color: "#007bff", textDecoration: "underline" },
  cartList: { listStyle: "none", padding: 0, marginTop: "10px" },
  cartItem: {
    display: "flex", alignItems: "center", backgroundColor: "#fff", borderRadius: "12px",
    padding: "12px", marginBottom: "12px", boxShadow: "0px 2px 6px rgba(0,0,0,0.05)"
  },
  itemImage: {
    width: "70px", height: "70px", borderRadius: "8px", objectFit: "cover", marginRight: "10px"
  },
  itemContent: { flex: 1 },
  itemTitle: { fontSize: "16px", fontWeight: "500", marginBottom: "4px", color: "#433b37" },
  itemActions: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  quantityControl: { display: "flex", alignItems: "center", gap: "6px" },
  quantityBtn: {
    border: "none", backgroundColor: "#e1e1e1", padding: "4px 10px",
    borderRadius: "6px", cursor: "pointer"
  },
  itemPrice: { color: "#6d5747", fontWeight: "600", fontSize: "15px" },
  optionList: {
    marginTop: "6px", fontSize: "14px", color: "#777", lineHeight: 1.4
  },
  removeBtn: {
    border: "none", backgroundColor: "#e57373", color: "white",
    padding: "8px", borderRadius: "6px", cursor: "pointer"
  },
  summarySection: {
    textAlign: "center", marginTop: "20px", fontSize: "16px", color: "#6d5747"
  },
  deliverySection: {
    textAlign: "center", marginTop: "24px"
  },
  methodButton: {
    padding: "12px 26px", margin: "10px auto", borderRadius: "12px", cursor: "pointer",
    border: "2px solid #1a237e", fontSize: "18px", fontWeight: "600",
    color: "#1a237e", display: "block", width: "100%",
    boxShadow: "0px 4px 10px rgba(26, 35, 126, 0.26)", transition: "0.3s"
  },
  submitButton: {
    backgroundColor: "#28a745", color: "white", padding: "14px", width: "100%",
    borderRadius: "8px", cursor: "pointer", marginTop: "15px", fontSize: "16px"
  },
  clearButton: {
    backgroundColor: "#ffa000", color: "white", padding: "12px", width: "100%",
    borderRadius: "8px", cursor: "pointer", marginTop: "10px", fontSize: "15px"
  },
  toast: {
    position: "fixed", top: "14px", left: "50%", transform: "translateX(-50%)",
    backgroundColor: "#6d5747", color: "#fff", padding: "10px 18px",
    borderRadius: "8px", zIndex: 1000, fontSize: "14px"
  },
  modalOverlay: {
    position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
    justifyContent: "center", alignItems: "center", zIndex: 1000
  },
  modal: {
    backgroundColor: "#fff8f1", padding: "28px", borderRadius: "16px",
    width: "92%", maxWidth: "380px", boxShadow: "0 8px 24px rgba(0,0,0,0.15)"
  },
  modalTitle: { fontSize: "18px", fontWeight: "700", marginBottom: "18px", textAlign: "center", color: "#6d5747" },
  modalSelect: {
    width: "100%", padding: "11px", fontSize: "15px",
    borderRadius: "8px", border: "1px solid #d3c1b2", marginBottom: "12px", backgroundColor: "#fff"
  },
  modalInput: {
    width: "100%", padding: "11px", fontSize: "15px",
    borderRadius: "8px", border: "1px solid #d3c1b2", marginBottom: "12px", backgroundColor: "#fff"
  },
  modalTextArea: {
    width: "100%", padding: "11px", fontSize: "15px",
    borderRadius: "8px", border: "1px solid #d3c1b2", marginBottom: "12px", backgroundColor: "#fff"
  },
  modalConfirmButton: {
    backgroundColor: "#2f855a", color: "white", padding: "13px", width: "100%",
    borderRadius: "8px", cursor: "pointer", fontSize: "16px", marginBottom: "10px"
  },
  modalCancelButton: {
    backgroundColor: "#e53e3e", color: "white", padding: "12px", width: "100%",
    borderRadius: "8px", cursor: "pointer", fontSize: "15px"
  },
  successContainer: { padding: "25px", textAlign: "center" },
  successTitle: { fontSize: "22px", color: "#388e3c", fontWeight: "700" },
  successText: { fontSize: "15px", marginTop: "5px", color: "#555" },
  summaryBox: {
    backgroundColor: "#fff8e1", padding: "15px", borderRadius: "10px",
    fontSize: "15px", color: "#6d5747", margin: "15px auto", maxWidth: "300px"
  },
  continueButton: {
    display: "inline-block", backgroundColor: "#388e3c", color: "white",
    padding: "12px 25px", borderRadius: "8px", textDecoration: "none", marginTop: "18px"
  },
};

export default CartPage;
